/**
 * Child Authentication Helper
 * 
 * COPPA-Compliant child authentication WITHOUT Firebase Auth
 * Children authenticate using:
 * - Family Code (8 characters)
 * - First Name
 * - 4-digit PIN
 * 
 * NO EMAIL OR PASSWORD COLLECTED FROM CHILDREN
 */

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  getDoc, 
  addDoc 
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hash a PIN for secure storage using simple but effective hashing
 * For production, PINs are salted with familyId for additional security
 */
function hashPIN(pin, salt = 'earnE2025') {
  // Simple hash function (for 4-digit PINs with salt)
  let hash = 0;
  const str = salt + pin + salt;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(16);
}

/**
 * Validate family code format (8 characters, alphanumeric)
 */
export function validateFamilyCode(code) {
  if (!code) return false;
  const cleaned = code.replace(/\s/g, '').toUpperCase();
  return /^[A-Z0-9]{8}$/.test(cleaned);
}

/**
 * Validate PIN format (4 digits)
 */
export function validatePIN(pin) {
  if (!pin) return false;
  return /^\d{4}$/.test(pin);
}

/**
 * Find family by invite code
 */
export async function findFamilyByCode(familyCode) {
  try {
    const cleanCode = familyCode.replace(/\s/g, '').toUpperCase();
    
    const familiesRef = collection(db, 'families');
    const q = query(familiesRef, where('inviteCode', '==', cleanCode));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error('Invalid family code. Please check the code and try again.');
    }

    const familyDoc = querySnapshot.docs[0];
    return {
      familyId: familyDoc.id,
      ...familyDoc.data()
    };
  } catch (error) {
    console.error('Error finding family:', error);
    throw error;
  }
}

/**
 * Get all children for a family
 */
export async function getChildrenForFamily(familyId) {
  try {
    const childrenRef = collection(db, 'families', familyId, 'children');
    const querySnapshot = await getDocs(childrenRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting children:', error);
    throw error;
  }
}

/**
 * Register a new child (parent-initiated or child self-registration)
 */
export async function registerChild(familyId, firstName, pin, dateOfBirth) {
  try {
    if (!firstName || firstName.trim().length < 2) {
      throw new Error('Please enter a valid first name (at least 2 characters)');
    }

    if (!validatePIN(pin)) {
      throw new Error('PIN must be exactly 4 digits');
    }

    const childrenRef = collection(db, 'families', familyId, 'children');
    const avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName)}&background=FF9800&color=fff&size=128`;

    // Create child profile
    const childDoc = await addDoc(childrenRef, {
      firstName: firstName.trim(),
      pinHash: hashPIN(pin),
      role: 'child',
      familyId: familyId,
      dateOfBirth: dateOfBirth || null, // ISO string e.g. "2010-05-15"
      createdAt: new Date().toISOString(),
      allowance: 0,
      totalEarned: 0,
      streak: 0,
      badges: [],
      avatar: avatar,
      lastLogin: new Date().toISOString()
    });

    return {
      childId: childDoc.id,
      firstName: firstName.trim(),
      familyId: familyId,
      dateOfBirth: dateOfBirth || null,
      avatar: avatar
    };
  } catch (error) {
    console.error('Error registering child:', error);
    throw error;
  }
}

/**
 * Authenticate an existing child
 */
export async function authenticateChild(familyId, childId, pin) {
  try {
    if (!validatePIN(pin)) {
      throw new Error('PIN must be 4 digits');
    }

    const childDocRef = doc(db, 'families', familyId, 'children', childId);
    const childDoc = await getDoc(childDocRef);

    if (!childDoc.exists()) {
      throw new Error('Child profile not found');
    }

    const childData = childDoc.data();
    const enteredPINHash = hashPIN(pin);

    if (childData.pinHash !== enteredPINHash) {
      throw new Error('Incorrect PIN. Please try again.');
    }

    // Update last login
    await setDoc(childDocRef, {
      lastLogin: new Date().toISOString()
    }, { merge: true });

    return {
      childId: childDoc.id,
      ...childData
    };
  } catch (error) {
    console.error('Error authenticating child:', error);
    throw error;
  }
}

/**
 * Store child session in localStorage (no Firebase Auth)
 */
export function setChildSession(childData) {
  try {
    const session = {
      type: 'child',
      childId: childData.childId,
      familyId: childData.familyId,
      firstName: childData.firstName,
      avatar: childData.avatar,
      loginTime: new Date().toISOString()
    };
    
    localStorage.setItem('earnE_childSession', JSON.stringify(session));
    
    // Set a flag that we're using child auth (not Firebase Auth)
    localStorage.setItem('earnE_authType', 'child');
  } catch (error) {
    console.error('Error storing child session:', error);
  }
}

/**
 * Get child session from localStorage
 */
export function getChildSession() {
  try {
    const authType = localStorage.getItem('earnE_authType');
    
    if (authType !== 'child') {
      return null;
    }
    
    const sessionData = localStorage.getItem('earnE_childSession');
    
    if (!sessionData) {
      return null;
    }
    
    return JSON.parse(sessionData);
  } catch (error) {
    console.error('Error getting child session:', error);
    return null;
  }
}

/**
 * Clear child session (logout)
 */
export function clearChildSession() {
  try {
    localStorage.removeItem('earnE_childSession');
    localStorage.removeItem('earnE_authType');
  } catch (error) {
    console.error('Error clearing child session:', error);
  }
}

/**
 * Check if there's an active child session
 */
export function hasActiveChildSession() {
  const session = getChildSession();
  return session !== null;
}

/**
 * Load child profile from Firestore
 */
export async function loadChildProfile(familyId, childId) {
  try {
    const childDocRef = doc(db, 'families', familyId, 'children', childId);
    const childDoc = await getDoc(childDocRef);

    if (!childDoc.exists()) {
      throw new Error('Child profile not found');
    }

    return {
      childId: childDoc.id,
      ...childDoc.data()
    };
  } catch (error) {
    console.error('Error loading child profile:', error);
    throw error;
  }
}

/**
 * Calculate age from date of birth string (ISO format: "YYYY-MM-DD")
 * Returns null if dateOfBirth is falsy
 */
export function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birth = new Date(dateOfBirth);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * COPPA compliance check: returns true if child is under 13
 * If dateOfBirth is missing, returns true (safe default → use pre-programmed bot)
 */
export function isUnder13(dateOfBirth) {
  const age = calculateAge(dateOfBirth);
  return age !== null && age < 13;
}

